// =============================================================================
// domain/patternPairing.ts — the two pairing matrices on Setup Quality.
// -----------------------------------------------------------------------------
// One setup can win or lose depending on how it is managed out. Both matrices on
// this tab ask that question from opposite ends:
//
//   Pattern × Exit-Model   — PRESCRIPTIVE. Which exit rule pays on which setup.
//   Pattern × Exit-Reason  — DIAGNOSTIC.  Which "why I got out" leaves the most
//                            money running after the exit.
//
// They share this module because they share everything that is easy to get
// wrong: the sample floor, the range-relative heat scale, "insufficient" versus
// "no trades", and the direction of good — which is INVERTED for the two
// extension readings, where a smaller number is the better outcome.
//
// ── 🚨 THE MATRIX AND THE LEADERBOARD ABOVE IT MUST COUNT THE SAME TRADES ──
//
// The old client filters this card to `annotated !== false` and filters the
// leaderboard three inches above it to nothing of the kind. So a trade that was
// imported, auto-tagged with a setup and an exit model, and not yet reviewed is
// one of the leaderboard's 18 VA Reversion trades and is missing from the matrix
// row underneath — one screen, one question, two populations (D102).
//
// That filter is also the exact line the annotation decouple removed from every
// quantitative surface: `countsForMetrics` is the seam, and `annotated !== false`
// is not to be re-added behind it. This module takes whatever population it is
// handed and never narrows it again. PD-23.
//
// ── ⚠ A CELL IS "INSUFFICIENT", NOT ZERO, AND THOSE ARE DIFFERENT WORDS ──
//
// Three states, three renderings, and collapsing any two of them is how a matrix
// lies quietly: no trades at all (`—`), some trades but below the floor
// ("insufficient"), and a real reading. A one-trade cell showing "100%" is the
// single most misleading thing this screen can print.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

import { median } from './excursionStats';
import { qualifiedExtLeg, type StoredExtension } from './leakExtension';

/** Below this a cell reads "insufficient" rather than a figure. Beta's `pemMinSample`. */
export const PAIRING_MIN_SAMPLE = 3;

/**
 * Below this many co-tagged trades the whole matrix refuses.
 *
 * ⚠ A 2×2 grid of one-trade cells is not a small version of this card, it is a
 * different and worse card — every cell reads 100% or 0% and the verdict names a
 * "strongest pairing" off a single trade.
 */
export const PAIRING_MIN_TOTAL = 10;

/** Cells at or above this are shown without the small-sample mark. */
export const PAIRING_NOISY_BELOW = 10;

export type PairingMetric = 'expectancy' | 'winrate' | 'totalpnl' | 'extension' | 'count';

export const PAIRING_METRICS: readonly { key: PairingMetric; label: string }[] = [
  { key: 'expectancy', label: 'Expectancy' },
  { key: 'winrate', label: 'Win Rate' },
  { key: 'totalpnl', label: 'Total P&L' },
  { key: 'extension', label: 'Extension' },
  { key: 'count', label: 'Sample' },
];

/**
 * ⚠ EXTENSION IS THE ONE WHERE SMALL IS GOOD. It measures what kept running
 * after you were already out, so the best-fitting exit model is the one that
 * leaves the LEAST behind. Every scale, every verdict and every colour in this
 * module reads this rather than assuming higher is better.
 */
export const PAIRING_LOWER_IS_BETTER: Record<PairingMetric, boolean> = {
  expectancy: false, winrate: false, totalpnl: false, extension: true, count: false,
};

export interface PairingTrade {
  patternId?: string | null;
  exitModelId?: string | null;
  netPnl?: number | null;
  qty?: number | null;
  extensions?: Record<string, StoredExtension> | null;
}

/** How a trade's money is read in the active display unit. `null` = no value. */
export type PairingValue = (t: PairingTrade) => number | null;

export interface PairingCell {
  count: number;
  wins: number;
  losses: number;
  /** Total in the display unit, over `valued` trades only. */
  totalPnl: number;
  /** How many of the cell's trades had a value in that unit. */
  valued: number;
  /** Creditable post-exit runs, in R, for the trades that carry a stored walk. */
  extR: number[];
}

export interface PairingAxis {
  id: string;
  name: string;
}

export interface PairingMatrix {
  rows: PairingAxis[];
  cols: PairingAxis[];
  /** Keyed `rowId + '|' + colId`. Absent = no trades in that pairing. */
  cells: Map<string, PairingCell>;
  /** Trades carrying BOTH tags — the matrix's own population. */
  total: number;
  /** In scope and missing one or both tags. Stated, never implied. */
  untagged: number;
  /** Of `total`, how many carry a stored walk the Extension metric can read. */
  withExtension: number;
}

export const cellKey = (rowId: string, colId: string): string => `${rowId}|${colId}`;

const emptyCell = (): PairingCell => ({
  count: 0, wins: 0, losses: 0, totalPnl: 0, valued: 0, extR: [],
});

/** The window every reader on this tab uses. */
const EXTENSION_WINDOW = '30';

/**
 * The creditable run for one trade, in R, or null.
 *
 * ⚠ THROUGH `qualifiedExtLeg`, never `ext.rMultiple` straight off the top. Where
 * no stop was derivable the top-level figure describes a raw peak — a run nobody
 * was holding — and Josh's rule is that a raw peak is never credited. The old
 * client's exit-reason heatmap reads the top level directly; this does not, so
 * one walk cannot mean two things on two cards.
 */
export function pairingExtensionR(t: PairingTrade): number | null {
  const leg = qualifiedExtLeg(t.extensions?.[EXTENSION_WINDOW] ?? null, 'stop');
  if (!leg) return null;
  const v = leg.rMultiple;
  // 🚨 `== null` FIRST. `Number(null)` is 0, so the `Number.isFinite(Number(x))`
  // idiom credits a walk with no R as a real reading of zero. Third file in this
  // repo to need the note.
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** How the trade ended, as stamped on the walk at compute time. Never re-derived. */
export function pairingExitReason(t: PairingTrade): string | null {
  const r = t.extensions?.[EXTENSION_WINDOW]?.exitReason;
  return typeof r === 'string' && r ? r : null;
}

/**
 * Build a matrix from a population and two axis resolvers.
 *
 * ⚠ THE AXES ARE DRIVEN BY THE DATA, not by the catalogue. An exit model the
 * user wrote but never tagged would be an empty column across every row, which
 * makes a five-column grid read as a twelve-column one with nothing in it. The
 * Exit Model Catalog shows unused models deliberately; a heatmap must not.
 *
 * ⚠ A row or column whose name will not resolve is still included, under
 * whatever `nameOf` returns — dropping trades because a catalogue entry was
 * deleted moves the totals and says nothing about why.
 */
export function buildPairingMatrix(
  trades: readonly PairingTrade[],
  colOf: (t: PairingTrade) => string | null,
  rowName: (id: string) => string,
  colName: (id: string) => string,
  unitValue: PairingValue,
): PairingMatrix {
  const cells = new Map<string, PairingCell>();
  const rowIds: string[] = []; const colIds: string[] = [];
  let total = 0; let untagged = 0; let withExtension = 0;

  for (const t of trades) {
    const rid = t.patternId ? String(t.patternId) : null;
    const cid = colOf(t);
    if (!rid || !cid) { untagged += 1; continue; }
    total += 1;
    if (!rowIds.includes(rid)) rowIds.push(rid);
    if (!colIds.includes(cid)) colIds.push(cid);

    const k = cellKey(rid, cid);
    const cell = cells.get(k) ?? emptyCell();
    cell.count += 1;

    // ⚠ WIN/LOSS CLASSIFY ON THE DOLLAR SIGN, the money on the display unit —
    // the same split the leaderboard above makes, for the same reason. Deriving
    // the sign per unit would turn a losing trade with no value in that unit into
    // a scratch and lift the win rate for a reason nobody could see.
    const pnl = t.netPnl ?? 0;
    if (pnl > 0) cell.wins += 1;
    else if (pnl < 0) cell.losses += 1;

    const uv = unitValue(t);
    if (uv != null) { cell.totalPnl += uv; cell.valued += 1; }

    const xr = pairingExtensionR(t);
    if (xr != null) { cell.extR.push(xr); withExtension += 1; }

    cells.set(k, cell);
  }

  return {
    rows: rowIds.map((id) => ({ id, name: rowName(id) })),
    cols: colIds.map((id) => ({ id, name: colName(id) })),
    cells, total, untagged, withExtension,
  };
}

/**
 * How many trades actually stand behind this cell UNDER THIS METRIC.
 *
 * 🚨 IT IS NOT ALWAYS `count`, AND THE OLD CLIENT'S FLOOR ASSUMES IT IS. The
 * Extension reading is a median of the trades that carry a stored post-exit
 * walk, and most trades do not — the walk costs a metered bar fetch. So a cell
 * of five trades can have exactly one measurable run, clear a floor of three on
 * its `count`, and print a confident median off that single walk.
 *
 * Same defect the leaderboard's average R had (PD-16's family): a figure resting
 * on a sample smaller than the row it sits in, with nothing on screen saying so.
 * Every floor in this module reads THIS, and the UI states it whenever it is
 * smaller than the cell.
 */
export function pairingCellSample(cell: PairingCell | undefined, metric: PairingMetric): number {
  if (!cell) return 0;
  return metric === 'extension' ? cell.extR.length : cell.count;
}

/**
 * One cell under one metric, or null when that metric cannot read it.
 *
 * ⚠ `null` FROM `expectancy` AND `totalpnl` WHEN NOTHING IN THE CELL HAD A VALUE
 * in the display unit — not 0. A pairing whose trades are all unpriceable is not
 * a pairing that broke even, and printing `$0` there is the same defect as the
 * `+0.00R` the leaderboard already fixed (PD-16).
 */
export function pairingCellValue(cell: PairingCell | undefined, metric: PairingMetric): number | null {
  if (!cell || cell.count === 0) return null;
  if (metric === 'count') return cell.count;
  if (metric === 'winrate') {
    const decided = cell.wins + cell.losses;
    // ⚠ A cell of nothing but scratches has NO win rate. Zero would read as
    // "every one of these lost", which is the opposite of what happened.
    return decided ? (100 * cell.wins) / decided : null;
  }
  if (metric === 'extension') return median(cell.extR);
  if (!cell.valued) return null;
  return metric === 'expectancy' ? cell.totalPnl / cell.valued : cell.totalPnl;
}

export type HeatBand = 'pos-3' | 'pos-2' | 'pos-1' | 'neu' | 'neg-1' | 'neg-2';

export interface HeatScale {
  min: number;
  max: number;
  hasRange: boolean;
  band: (v: number | null) => HeatBand;
}

/**
 * A range-relative scale across the cells that clear the sample floor.
 *
 * ⚠ ONLY THOSE CELLS SET THE RANGE. A one-trade cell at +$900 would otherwise
 * stretch the scale it is not even allowed to be coloured by, and every real
 * reading would collapse into the middle band.
 *
 * ⚠ WITH NO RANGE — one qualifying cell, or all of them identical — EVERY cell
 * is neutral. Colouring a single cell "strongest" against nothing is a claim.
 */
export function pairingHeatScale(
  matrix: PairingMatrix, metric: PairingMetric, minSample = PAIRING_MIN_SAMPLE,
): HeatScale {
  let min = Infinity; let max = -Infinity;
  for (const r of matrix.rows) {
    for (const c of matrix.cols) {
      const cell = matrix.cells.get(cellKey(r.id, c.id));
      if (pairingCellSample(cell, metric) < minSample) continue;
      const v = pairingCellValue(cell, metric);
      if (v == null) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  const hasRange = Number.isFinite(min) && Number.isFinite(max) && max > min;
  const lower = PAIRING_LOWER_IS_BETTER[metric];

  const band = (v: number | null): HeatBand => {
    if (v == null || !hasRange) return 'neu';
    let t = (v - min) / (max - min);
    if (lower) t = 1 - t;
    // ⚠ Sample is an INTENSITY, not a verdict — three shades of the same colour.
    // Scoring a rarely-traded pairing "weak" for being rare would be a claim
    // about its edge made from its frequency.
    if (metric === 'count') return t >= 0.66 ? 'pos-3' : t >= 0.33 ? 'pos-2' : 'pos-1';
    if (t >= 0.80) return 'pos-3';
    if (t >= 0.62) return 'pos-2';
    if (t >= 0.50) return 'pos-1';
    if (t >= 0.38) return 'neu';
    if (t >= 0.20) return 'neg-1';
    return 'neg-2';
  };

  return { min, max, hasRange, band };
}

export interface PairingPick {
  row: PairingAxis;
  col: PairingAxis;
  cell: PairingCell;
  value: number;
}

export interface PairingBestWorst {
  best: PairingPick | null;
  worst: PairingPick | null;
  /** How many cells cleared the floor. One means there is nothing to compare. */
  qualifying: number;
}

/**
 * Strongest and weakest pairing above the sample floor.
 *
 * ⚠ `qualifying` TRAVELS WITH THEM. "Strongest pairing: X" off the only cell
 * that cleared the floor is not a comparison, and the screen has to be able to
 * say so — the old client's own verdict does, and it is the difference between a
 * ranking and a single observation dressed as one.
 */
export function pairingBestWorst(
  matrix: PairingMatrix, metric: PairingMetric, minSample = PAIRING_MIN_SAMPLE,
): PairingBestWorst {
  // ⚠ Sample ranks by EXPECTANCY. "Your strongest pairing is the one you traded
  // most" is not a finding, and the old client makes the same substitution.
  const m: PairingMetric = metric === 'count' ? 'expectancy' : metric;
  const lower = PAIRING_LOWER_IS_BETTER[m];
  let best: PairingPick | null = null;
  let worst: PairingPick | null = null;
  let qualifying = 0;

  for (const r of matrix.rows) {
    for (const c of matrix.cols) {
      const cell = matrix.cells.get(cellKey(r.id, c.id));
      if (!cell || pairingCellSample(cell, m) < minSample) continue;
      const v = pairingCellValue(cell, m);
      if (v == null) continue;
      qualifying += 1;
      const pick: PairingPick = { row: r, col: c, cell, value: v };
      if (best == null || (lower ? v < best.value : v > best.value)) best = pick;
      if (worst == null || (lower ? v > worst.value : v < worst.value)) worst = pick;
    }
  }
  return { best, worst, qualifying };
}

// ── Pattern × Exit-Reason ────────────────────────────────────────────────────

/**
 * Rows ordered by one extreme cell each — `'best'` puts the strongest row on
 * top, `'worst'` puts the most damaging one there.
 *
 * 🚨 `'worst'` IS NOT `'best'` REVERSED, and getting that wrong silently inverts
 * a whole card. Each row is scored by a DIFFERENT cell under the two options: by
 * its best pairing, or by its worst. A book where one setup has both the best
 * and the worst cell on the grid orders differently under each, and the
 * exit-reason card wants "which setup is being cut shortest" — its worst cell —
 * at the top.
 *
 * ⚠ Rows with no qualifying cell sink rather than disappear: a setup whose every
 * pairing is below the floor is a fact about the sample, and dropping the row
 * makes the grid look denser than the data is.
 */
export function orderRowsByExtremeCell(
  matrix: PairingMatrix, metric: PairingMetric,
  pick: 'best' | 'worst' = 'best', minSample = PAIRING_MIN_SAMPLE,
): PairingAxis[] {
  // `higherWins` = which end of the scale this pick is looking for.
  const higherWins = PAIRING_LOWER_IS_BETTER[metric] === (pick === 'worst');
  const scoreOf = (rowId: string): number | null => {
    let s: number | null = null;
    for (const c of matrix.cols) {
      const cell = matrix.cells.get(cellKey(rowId, c.id));
      if (pairingCellSample(cell, metric) < minSample) continue;
      const v = pairingCellValue(cell, metric);
      if (v == null) continue;
      if (s == null || (higherWins ? v > s : v < s)) s = v;
    }
    return s;
  };
  return [...matrix.rows].sort((a, b) => {
    const sa = scoreOf(a.id); const sb = scoreOf(b.id);
    if (sa == null && sb == null) return 0;
    if (sa == null) return 1;
    if (sb == null) return -1;
    return higherWins ? sb - sa : sa - sb;
  });
}

/** The old client's four, in its order. Only reasons actually seen become columns. */
export const PAIRING_EXIT_REASONS: readonly { key: string; label: string }[] = [
  { key: 'target', label: 'Target' },
  { key: 'stop', label: 'Stop' },
  { key: 'discretionary', label: 'Discretion' },
  { key: 'time', label: 'Time' },
];

export interface ExitReasonRead {
  pattern: string;
  reasonKey: string;
  reasonLabel: string;
  medianR: number;
  sample: number;
  /** What to do about it — keyed off the REASON, because the fix differs by it. */
  action: string;
}

/**
 * Below this the coaching line stays silent.
 *
 * ⚠ Half a unit of risk. Under it the honest reading is "your exits are roughly
 * where the move ended", and telling someone to hold longer off a +0.2R median
 * is advice that costs money on the next head-fake.
 */
export const EXIT_REASON_READ_MIN_R = 0.5;

const ACTION_BY_REASON: Record<string, (pattern: string) => string> = {
  target: (p) => `your target on ${p} may be too tight — test a wider target or a partial runner`,
  stop: (p) => `these ${p} trades stopped you out and then ran — look at entry timing and stop placement, not the target`,
  discretionary: (p) => `you're cutting ${p} early by feel — let it reach a defined target`,
  time: (p) => `you're time-stopping ${p} before it develops — give it more room or a later cutoff`,
};

/**
 * The one line worth saying about an exit-reason matrix, or null.
 *
 * ⚠ NULL IS A REAL ANSWER and the card must print nothing rather than reach for
 * the least-bad cell. A coaching sentence generated because the template existed
 * is exactly the failure the controllable-loss card shipped in the old client
 * (PD-22) — a conclusion whose own numbers do not support it.
 */
export function exitReasonRead(
  matrix: PairingMatrix, minSample = PAIRING_MIN_SAMPLE, minR = EXIT_REASON_READ_MIN_R,
): ExitReasonRead | null {
  let top: ExitReasonRead | null = null;
  for (const r of matrix.rows) {
    for (const c of matrix.cols) {
      const cell = matrix.cells.get(cellKey(r.id, c.id));
      // ⚠ THE FLOOR IS THE WALKS, not the trades — see `pairingCellSample`.
      if (!cell || cell.extR.length < minSample) continue;
      const m = median(cell.extR);
      if (m == null || m < minR) continue;
      if (top && m <= top.medianR) continue;
      const build = ACTION_BY_REASON[c.id];
      top = {
        pattern: r.name,
        reasonKey: c.id,
        reasonLabel: c.name,
        medianR: m,
        sample: cell.extR.length,
        action: build ? build(r.name) : `review your ${r.name} exits`,
      };
    }
  }
  return top;
}
